import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  format,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  subMonths,
} from 'date-fns';

export interface DashboardJob {
  id: string;
  title: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  status: string;
  client_name: string | null;
  address: string | null;
  service_id: string | null;
  team_member_id: string | null;
  created_at?: string;
  updated_at?: string;
  // Pay to book (select('*') brings these back).
  payment_method?: string | null;
  payment_status?: string | null;
  payment_amount_paid?: number | string | null;
  payment_total?: number | string | null;
  payment_hold_expires_at?: string | null;
}

export interface DashboardService {
  id: string;
  name: string;
  price: string | number | null;
}

export interface DashboardTeamMember {
  id: string;
  name: string;
}

export interface DashboardEnquiry {
  id: string;
  status: string;
  created_at: string;
}

export interface DashboardData {
  upcomingJobs: DashboardJob[];
  futureJobs: DashboardJob[];
  allJobs: DashboardJob[];
  lastMonthJobs: DashboardJob[];
  last30DaysJobs: DashboardJob[];
  services: DashboardService[];
  teamMembers: DashboardTeamMember[];
  enquiries: DashboardEnquiry[];
  lastMonthEnquiries: DashboardEnquiry[];
}

const subDays = (date: Date, days: number) => addDays(date, -days);

/**
 * Loads all data required by the admin Dashboard in a single parallel fetch.
 * Enabled only when orgId is set AND the org is done loading — matching the
 * original `if (!orgLoading) fetchDashboardData()` gate.
 */
export function useDashboardData(
  orgId: string | null | undefined,
  orgLoading: boolean,
) {
  return useQuery({
    queryKey: ['dashboard', orgId],
    enabled: Boolean(orgId) && !orgLoading,
    queryFn: async (): Promise<DashboardData> => {
      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(today), 'yyyy-MM-dd');
      // Future jobs scheduled after this week, up to a 90-day horizon. Powers the
      // "Upcoming" section of the dashboard's Upcoming Jobs card.
      const afterWeek = format(addDays(endOfWeek(today), 1), 'yyyy-MM-dd');
      const futureHorizon = format(addDays(today, 90), 'yyyy-MM-dd');
      const monthStart = format(startOfMonth(today), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(today), 'yyyy-MM-dd');
      const lastMonthStart = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
      const lastMonthEnd = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
      const thirtyDaysAgo = format(subDays(today, 30), 'yyyy-MM-dd');

      const [
        upcomingJobsResult,
        futureJobsResult,
        monthJobsResult,
        lastMonthJobsResult,
        last30DaysJobsResult,
        servicesResult,
        teamResult,
        enquiriesResult,
        lastMonthEnquiriesResult,
      ] = await Promise.all([
        supabase
          .from('jobs')
          .select('*')
          .eq('organization_id', orgId!)
          .gte('scheduled_date', todayStr)
          .lte('scheduled_date', weekEnd)
          .order('scheduled_date', { ascending: true })
          .order('start_time', { ascending: true }),
        supabase
          .from('jobs')
          .select('*')
          .eq('organization_id', orgId!)
          .gte('scheduled_date', afterWeek)
          .lte('scheduled_date', futureHorizon)
          .order('scheduled_date', { ascending: true })
          .order('start_time', { ascending: true }),
        supabase
          .from('jobs')
          .select('*')
          .eq('organization_id', orgId!)
          .gte('scheduled_date', monthStart)
          .lte('scheduled_date', monthEnd),
        supabase
          .from('jobs')
          .select('*')
          .eq('organization_id', orgId!)
          .gte('scheduled_date', lastMonthStart)
          .lte('scheduled_date', lastMonthEnd),
        supabase
          .from('jobs')
          .select('*')
          .eq('organization_id', orgId!)
          .gte('scheduled_date', thirtyDaysAgo)
          .lte('scheduled_date', todayStr),
        supabase
          .from('services')
          .select('id, name, price')
          .eq('organization_id', orgId!),
        supabase
          .from('team_members')
          .select('id, name')
          .eq('organization_id', orgId!),
        supabase
          .from('enquiries')
          .select('id, status, created_at')
          .eq('organization_id', orgId!)
          .gte('created_at', monthStart),
        supabase
          .from('enquiries')
          .select('id, status, created_at')
          .eq('organization_id', orgId!)
          .gte('created_at', lastMonthStart)
          .lt('created_at', monthStart),
      ]);

      // Preserve original behaviour: ignore errors and fall back to empty arrays.
      return {
        upcomingJobs: (upcomingJobsResult.data ?? []) as DashboardJob[],
        futureJobs: (futureJobsResult.data ?? []) as DashboardJob[],
        allJobs: (monthJobsResult.data ?? []) as DashboardJob[],
        lastMonthJobs: (lastMonthJobsResult.data ?? []) as DashboardJob[],
        last30DaysJobs: (last30DaysJobsResult.data ?? []) as DashboardJob[],
        services: (servicesResult.data ?? []) as DashboardService[],
        teamMembers: (teamResult.data ?? []) as DashboardTeamMember[],
        enquiries: (enquiriesResult.data ?? []) as DashboardEnquiry[],
        lastMonthEnquiries: (lastMonthEnquiriesResult.data ?? []) as DashboardEnquiry[],
      };
    },
  });
}
